import { useCallback, useEffect, useRef, useState } from 'react'

import type { RowClickedEvent } from 'ag-grid-community'
import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement } from 'react'
import type { InstantSearchApi } from 'react-instantsearch'
import type { Props as RefinementListProps } from '~/components/Algolia/RefinementList'
import type { IIap, IAP_STATUS, IAP_SUB_TYPE } from '~/types/iap'

import getColumns from './columns'

import PanelContent from '../PanelContent'
import SynchronizeButton from '../SynchronizeButton'

import AlgoliaTable from '~/components/Algolia/Table'
import ContentLayout from '~/components/ContentLayout'
import Header from '~/components/Header/Classic'
import Layout from '~/components/Layouts/Layout'
import Panel from '~/components/Panel'
import ViewTitle from '~/components/ViewTitle'
import { IAP_STATUS_LABEL_MAPPING, IAP_SUB_TYPE_LABEL_MAPPING } from '~/const'
import { ALGOLIA_INDEX_NAMES } from '~/const/algolia'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useAppSummary } from '~/providers/AppSummary'
import { useAppSummaryDetailsQueryV2 } from '~/queries/applications'
import { getIapFilters } from '~/utils/algolia'
import { transformByDeviceType } from '~/utils/application'
import { getNumberWithSpaces } from '~/utils/number'
import { isFocusedCellActions } from '~/utils/table'

import './styles.scss'

const ENTRIES_NAME = 'iap-subscriptions'

const refinements: RefinementListProps[] = [
  {
    attribute: 'sub_type',
    label: 'Type',
    transformItems: (items) =>
      items.map((item) => ({
        ...item,
        label: IAP_SUB_TYPE_LABEL_MAPPING[item.label as IAP_SUB_TYPE] || item.label,
      })),
  },
  {
    attribute: 'status',
    label: 'Status',
    transformItems: (items) =>
      items.map((item) => ({
        ...item,
        label: IAP_STATUS_LABEL_MAPPING[item.label as IAP_STATUS] || item.label,
      })),
  },
  {
    attribute: 'price',
    label: 'Price',
    transformItems: (items) =>
      items.map((item) => ({
        ...item,
        label: `$ ${getNumberWithSpaces(item.label)}`,
      })),
  },
  { attribute: 'subscription_group_name', label: 'Subscription group name' },
  {
    attribute: 'subscription_period',
    label: 'Subscription period',
  },
  { attribute: 'trial_period', label: 'Trial period' },
]

const IapSubscriptions = (): ReactElement => {
  /** Local state */

  const gridRef = useRef<AgGridReact>(null)

  const tableRef = useRef<HTMLDivElement>(null)

  const [instantSearchApi, setInstantSearchApi] = useState<InstantSearchApi>()

  const [panelRef, setPanelRef] = useState<HTMLDivElement>()

  const [selectedIap, setSelectedIap] = useState<IIap>()

  const { appDetails, appKey, deviceType, isLoading: isAppSummaryLoading } = useAppSummary()

  /** Queries & Mutations */

  const { data: appSummary } = useAppSummaryDetailsQueryV2(appKey, deviceType)

  /** Effects */

  useDocumentTitle('IAP Subscriptions')

  const handleClosePanel = useCallback(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.deselectAll()
    }
    setSelectedIap(undefined)
  }, [])

  const handleClickOutside = useCallback(
    (event: any) => {
      if (
        (panelRef &&
          tableRef.current &&
          (tableRef.current?.contains(event.target) || panelRef.contains(event.target))) ||
        !document.getElementById('root')?.contains(event.target)
      ) {
        return
      }
      handleClosePanel()
    },
    [panelRef, handleClosePanel]
  )

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [panelRef, handleClickOutside])

  /** Handlers */

  const configurationFilters = getIapFilters({ appKey, deviceType })

  const handleRowClicked = useCallback((evt: RowClickedEvent) => {
    if (!isFocusedCellActions(evt)) {
      const { data } = evt
      setSelectedIap(data)
    }
  }, [])

  const handleSynchronizeSuccess = () => {
    instantSearchApi?.refresh()
    gridRef.current?.api.refreshCells({ force: true })
  }

  const handleTableInit = (instantSearchApi: InstantSearchApi) => setInstantSearchApi(instantSearchApi)

  const contentRefCallback = (node: HTMLDivElement) => setPanelRef(node)

  /** Render */

  const appSelectorApp = appDetails ? transformByDeviceType(appDetails, deviceType) : undefined

  const isSwitchDisabled = !appDetails?.android || !appDetails?.ios

  return (
    <>
      <Header apps={appSelectorApp ? [appSelectorApp] : []} isSwitchDisabled={isSwitchDisabled} />
      <Layout className="iap-subscriptions-view" withPadding>
        <ViewTitle className="layout-view-title">
          <span>In-App purchases / Subscriptions</span>
          <SynchronizeButton
            appId={appSummary?.app_id}
            deviceType={deviceType}
            onSynchronizeSuccess={handleSynchronizeSuccess}
          />
        </ViewTitle>
        <ContentLayout className="iap-subscriptions-view__content" loading={isAppSummaryLoading}>
          <AlgoliaTable
            className="with-min-height"
            columnDefs={getColumns(deviceType)}
            configurationFilters={configurationFilters}
            entriesName={ENTRIES_NAME}
            hitsPerPage={10}
            indexName={ALGOLIA_INDEX_NAMES.IAP_SUBSCRIPTIONS}
            onInit={handleTableInit}
            onRowClicked={handleRowClicked}
            ref={gridRef}
            refinements={refinements}
            tableRef={tableRef}
            withSearchBarAutoFocus
          />
        </ContentLayout>
        <Panel
          className="iap-panel"
          isOpen={!!selectedIap}
          onClosePanel={handleClosePanel}
          panelRef={contentRefCallback}
        >
          {selectedIap ? <PanelContent iap={selectedIap} /> : null}
        </Panel>
      </Layout>
    </>
  )
}

export default IapSubscriptions
